import { useEditor, useNode } from "@craftjs/core";
import { styled } from '@mui/system';
import {
  Button as MaterialButton,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  ButtonProps,
  TextField,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import { useEffect } from "react";
import { ImageUploader } from "./ImageUploader";

interface CButtonProps {
  text?: string;
  fontSize?: number;
  color?: any;
  backgroundColor?: any;
  width?: any;
  height?: any;
  paddingV?: any;
  paddingH?: any;
  borderRadius?: any;
  borderColor?: any;
  borderWidth?: any;
  fontWeight?: any;
  type?: any;
  backgroundImage?: string;
  fontFamily?: string;
  [key: string]: any;
}

export const CButton = ({ 
  text,
  fontSize,
  color,
  backgroundColor,
  width,
  height,
  paddingV,
  paddingH,
  borderRadius,
  borderColor,
  borderWidth,
  fontWeight,
  type,
  backgroundImage, 
  fontFamily,
  ...props 
}: CButtonProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions ,
  } = useNode((node) => ({
    selected: node.events.selected,
    actions: node.data.props.actions,
  }));

  const { enabled } = useEditor(
    (state, query) => ({
      enabled: state.options.enabled,
    })
  );

  const pmstr = (value: any) => {
    return (!isNaN(value) && !isNaN(parseFloat(value))) ? `${value}px` : value;
  }

  return (
    <MaterialButton
      ref={(ref: HTMLButtonElement) => connect(drag(ref))}
      type={enabled ? 'button' : type}
      style={{
        fontSize: `${fontSize}px`,
        color: color,
        backgroundColor: backgroundColor,
        width: width,
        height: height,
        padding: `${pmstr(paddingV)} ${pmstr(paddingH)} ${pmstr(paddingV)} ${pmstr(paddingH)}`, 
        borderColor: selected ? '#008522' : borderColor,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        borderRadius: borderRadius,
        borderWidth: borderWidth,
        fontWeight: fontWeight,
        borderStyle: "solid",
        minWidth: '0px',
        fontFamily: fontFamily,
      }}
      {...props}
    >
      {text}
    </MaterialButton>
  );
};

export const CButtonSettings = () => {
  const {
    text,
    fontSize,
    color,
    backgroundColor,
    width,
    height,
    paddingV,
    paddingH,
    borderRadius,
    borderColor,
    borderWidth,
    fontWeight,
    type,
    backgroundImage,
    fontFamily,
    actions: { setProp },
  } = useNode((node) => ({
    text: node.data.props.text,
    fontSize: node.data.props.fontSize,
    color: node.data.props.color,
    backgroundColor: node.data.props.backgroundColor,
    width: node.data.props.width,
    height: node.data.props.height,
    paddingV: node.data.props.paddingV,
    paddingH: node.data.props.paddingH,
    borderRadius: node.data.props.borderRadius,
    borderColor: node.data.props.borderColor,
    borderWidth: node.data.props.borderWidth,
    fontWeight: node.data.props.fontWeight,
    type: node.data.props.type,
    backgroundImage: node.data.props.backgroundImage,
    fontFamily: node.data.props.fontFamily,
  }));

  return (
    <Stack spacing={2} paddingBottom={4}>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Text</FormLabel>
          <TextField
            value={text}
            onChange={(e) =>
              setProp((props: any) => (props.text = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Font size</FormLabel>
          <TextField
            value={fontSize}
            onChange={(e) =>
              setProp((props: any) => (props.fontSize = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} variant="outlined">
          <FormLabel component="legend">Font Family</FormLabel>
          <Select
            value={fontFamily}
            onChange={(e) => setProp((props: any) => (props.fontFamily = e.target.value))}
          >
            <MenuItem value="Open Sans,sans-serif">Open Sans</MenuItem>
            <MenuItem value="Arial, sans-serif">Arial</MenuItem>
            <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
            <MenuItem value="'Georgia', serif">Georgia</MenuItem>
            <MenuItem value="'Times New Roman', Times, serif">Times New Roman</MenuItem>
            <MenuItem value="'Trebuchet MS', sans-serif">Trebuchet MS</MenuItem>
            <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <FormControl component="fieldset">
        <FormLabel component="legend">Font Weight</FormLabel>
        <RadioGroup
          defaultValue={fontWeight}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.fontWeight = e.target.value))
          }
        >
          <FormControlLabel
            label="100"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="200"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="300"
            value="300"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="400"
            value="400"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="500"
            value="500"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="600"
            value="600"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="700"
            value="700"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="800"
            value="800"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="900"
            value="900"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Background Color</FormLabel>
          <MuiColorInput format='hex' value={backgroundColor} onChange={(color) => {
            setProp((props: any) => (props.backgroundColor = color), 500);
          }}
        />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Color</FormLabel>
          <MuiColorInput format='hex' value={color} onChange={(color) => {
            setProp((props: any) => (props.color = color), 500);
          }}
        />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Background Image</FormLabel>
          <ImageUploader onImageChange={(url: string) => setProp((props: any) => (props.backgroundImage = url), 500)} />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Width</FormLabel>
          <TextField
            value={width || ''}
            onChange={(e) =>
              setProp((props: any) => (props.width = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Height</FormLabel>
          <TextField
            value={height || ''}
            onChange={(e) =>
              setProp((props: any) => (props.height = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Vertical</FormLabel>
          <TextField
            value={paddingV}
            onChange={(e) =>
              setProp((props: any) => (props.paddingV = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Horizontal</FormLabel>
          <TextField
            value={paddingH}
            onChange={(e) =>
              setProp((props: any) => (props.paddingH = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Width</FormLabel>
          <TextField
            value={borderWidth}
            onChange={(e) =>
              setProp((props: any) => (props.borderWidth = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Color</FormLabel>
          <MuiColorInput format='hex' value={borderColor} onChange={(color) => {
            setProp((props: any) => (props.borderColor = color), 500);
          }} />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Radius</FormLabel>
          <TextField
            value={borderRadius}
            onChange={(e) =>
              setProp((props: any) => (props.borderRadius = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <FormControl fullWidth={true} component="fieldset">
        <FormLabel component="legend">Type</FormLabel>
        <RadioGroup
          defaultValue={type}
          onChange={(e) =>
            setProp((props: any) => (props.type = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '30px' }}
        >
          <FormControlLabel
            label="Button"
            value="button"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Submit"
            value="submit"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
    </Stack>
  );
};

export const CButtonDefaultProps: CButtonProps = {
  text: 'Button',
  backgroundColor: '#008522',
  color: '#FFFFFF',
  width: 'auto',
  height: 'auto',
  borderRadius: '4px',
  fontSize: 14,
  fontWeight: '300',
  type: 'button',
  // fontFamily: 'Open Sans,sans-serif',
};

CButton.craft = {
  displayName: "Button",
  props: CButtonDefaultProps,
  related: {
    settings: CButtonSettings,
  },
};
